import { useState } from 'react';
import { Head, useAdmin } from '../ui/Shell';
import { Block, Cell, Grid, HBars, Kpis, Placeholder, Table, type Tone } from '../ui/Block';
import { fmtBytes, fmtDate, get } from '../lib/api';
import { describeErr, hhmmss, useData } from '../lib/hooks';
import { openCustomer } from './CustomerDrawer';

interface StorageTotals { artifacts: number; bytes: number; bytesWeek: number; failed: number; staleUploads: number; byKind: { kind: string; artifacts: number; bytes: number }[]; byUser: { email: string | null; userId: string; artifacts: number; bytes: number }[] }
interface Artifact { id: string; kind: string; state: string; bytes: number; storageKey: string | null; createdAt: string; codec: string | null; container: string | null; durationS: number | null }

const VX = ['var(--vx-1)', 'var(--vx-2)', 'var(--vx-3)', 'var(--vx-4)', 'var(--vx-5)', 'var(--vx-6)'];
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ART_TONE: Record<string, Tone> = { sealed: 'ok', uploading: 'info', failed: 'bad', deleted: 'mute' };

export function Storage() {
  const ctx = useAdmin();
  const { env, paused } = ctx;
  const [q, setQ] = useState('');
  const totals = useData<StorageTotals>(env, '/admin/storage', 30, paused);
  const rid = q.trim().toLowerCase();
  const rec = useData<{ id: string; artifacts: Artifact[] }>(env, UUID.test(rid) ? `/admin/recordings/${rid}` : null, 0, paused);
  const t = totals.data;
  const err = describeErr(totals.error) ? { ...describeErr(totals.error)!, retry: totals.reload } : null;

  const signedLink = async (key: string) => {
    try {
      const r = await get<{ url: string; ttlS: number }>(env, `/admin/storage/signed-link?key=${encodeURIComponent(key)}`);
      window.open(r.url, '_blank', 'noopener');
      ctx.toast('Signed link opened', `Valid for ${Math.round(r.ttlS / 60)} minutes · ${key}`);
    } catch (e) { ctx.toast((e as Error).message, '', { logged: false }); }
  };

  return (
    <>
      <Head title="Storage" sub="Totals come from the database, not from a bucket listing." />
      <Grid>
        <Cell span={12}>
          <Block title="Totals" asOf={hhmmss(totals.at)} stale={totals.ageS > 60} loading={totals.loading && !t} error={err}>
            {t && <Kpis items={[
              { label: 'TOTAL', v: fmtBytes(t.bytes), sub: 'sealed artifacts' },
              { label: 'ARTIFACTS', v: t.artifacts.toLocaleString() },
              { label: 'ADDED, 7 DAYS', v: fmtBytes(t.bytesWeek) },
              { label: 'FAILED', v: t.failed.toLocaleString(), fg: t.failed ? 'var(--conf-low)' : undefined, dot: t.failed ? 'bad' : undefined },
              { label: 'STALE UPLOADS', v: t.staleUploads.toLocaleString(), fg: t.staleUploads ? 'var(--conf-mid)' : undefined, dot: t.staleUploads ? 'warn' : undefined },
            ]} />}
          </Block>
        </Cell>
        <Cell span={6}>
          <Block title="By artifact kind" asOf={hhmmss(totals.at)} loading={totals.loading && !t} error={err} empty={t && t.byKind.length === 0 ? 'No artifacts.' : undefined}>
            {t && <HBars rows={t.byKind.map((k, i) => ({ label: k.kind, n: k.bytes, v: `${fmtBytes(k.bytes)} · ${k.artifacts.toLocaleString()}`, bg: VX[i % VX.length], mono: true }))} />}
          </Block>
        </Cell>
        <Cell span={6}>
          <Block title="By user" asOf={hhmmss(totals.at)} loading={totals.loading && !t} error={err} empty={t && t.byUser.length === 0 ? 'No artifacts.' : undefined}>
            {t && <Table cols={[{ label: 'USER', width: 'minmax(160px,1.5fr)' }, { label: 'ARTIFACTS', width: '90px', align: 'r' }, { label: 'BYTES', width: '90px', align: 'r' }]}
              rows={t.byUser.map((u) => ({
                key: u.userId, onClick: () => openCustomer(ctx, u.userId),
                cells: [{ t: u.email || '(no email)', sub: <span className="mono">{u.userId}</span> }, { t: u.artifacts.toLocaleString(), mono: true }, { t: fmtBytes(u.bytes), mono: true }],
              }))} />}
          </Block>
        </Cell>
        <Cell span={12}>
          <Block title="Artifact browser" sub="Rows from the artifacts table. A signed link opens the object for five minutes." search={{ value: q, placeholder: 'Recording id', onChange: setQ }}
            loading={rec.loading && !rec.data && UUID.test(rid)} error={describeErr(rec.error) ? { ...describeErr(rec.error)!, retry: rec.reload } : null}
            empty={!UUID.test(rid) ? (rid ? 'That is not a recording id.' : 'Type a recording id.') : rec.data && rec.data.artifacts.length === 0 ? `No artifacts on ${rid}.` : undefined}>
            {rec.data && <Table minWidth={860} cols={[{ label: 'KIND', width: 'minmax(110px,.9fr)' }, { label: 'STATE', width: '100px' }, { label: 'BYTES', width: '90px', align: 'r' }, { label: 'STORAGE KEY', width: 'minmax(260px,2.4fr)' }, { label: 'CREATED', width: '110px' }, { label: '', width: 'minmax(100px,auto)', align: 'r' }]}
              rows={rec.data.artifacts.map((a) => ({
                key: a.id,
                cells: [
                  { t: a.kind, mono: true, fw: 500, sub: [a.container, a.codec].filter(Boolean).join(' · ') || undefined },
                  { pill: { label: a.state.toUpperCase(), tone: ART_TONE[a.state] ?? 'mute' } },
                  { t: a.bytes ? fmtBytes(a.bytes) : '—', mono: true },
                  { t: a.storageKey ?? '—', mono: true, fg: a.storageKey ? undefined : 'var(--ink-40)' },
                  { t: fmtDate(a.createdAt) },
                  { acts: a.state === 'sealed' && a.storageKey ? [{ label: 'Signed link', onClick: () => void signedLink(a.storageKey!) }] : [] },
                ],
              }))} />}
          </Block>
        </Cell>
        <Cell span={12}>
          <Block title="Bucket inventory">
            <Placeholder text="Bucket inventory: not built yet" sub="Listing the whole Tigris bucket and finding orphaned keys is out of scope for this version. Totals above come from the database only." />
          </Block>
        </Cell>
      </Grid>
    </>
  );
}
